import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { Search, Loader, X, Package, Sparkles, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce';
import { apogeeApiService } from '../../services/apogeeApi';
import type { PackRow, PackSearchHit } from '../../types/apogee';
import { ApogeeSearchDisabledError, SEARCH_PACKS_MAX_LIMIT } from '../../types/apogee';
import { apogeeIdFromSearchHit, packRowFromSearchHit } from '../../utils/packSearch';
import { cn } from '../../utils/cn';
import { packDetailPath } from '../../utils/packRoutes';

export interface ApogeePackSearchFieldProps {
  /** Selected Apogee internal id (`apogee_id`), or null if none. */
  value: number | null;
  onChange: (apogeeId: number | null) => void;
  label?: string;
  /** Hint under the field */
  description?: string;
  disabled?: boolean;
  className?: string;
  /** Smaller padding / typography for dense forms */
  compact?: boolean;
}

function hitDisplayName(hit: PackSearchHit): string {
  return hit._source.name?.trim() || 'Untitled pack';
}

function hitSummarySnippet(hit: PackSearchHit): string {
  const s = hit._source.summary?.trim();
  if (!s) return '';
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}

export function ApogeePackSearchField({
  value,
  onChange,
  label = 'Linked modpack (Apogee)',
  description = 'Search the Apogee index (GET /v1/search/packs) by name, slug, platforms, and more. The pack’s internal Apogee id is stored when you confirm a selection.',
  disabled = false,
  className,
  compact = false,
}: ApogeePackSearchFieldProps) {
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 280);
  const [hits, setHits] = useState<PackSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [selectedPack, setSelectedPack] = useState<PackRow | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);

  useEffect(() => {
    if (value == null) {
      setSelectedPack(null);
      return;
    }
    let cancelled = false;
    setLoadingSelected(true);
    apogeeApiService
      .getPackByApogeeId(value)
      .then((p) => {
        if (!cancelled) setSelectedPack(p);
      })
      .catch(() => {
        if (!cancelled) setSelectedPack(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSelected(false);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setSearchError(null);
      return;
    }

    const ac = new AbortController();
    setSearching(true);
    setSearchError(null);

    apogeeApiService
      .searchPacks({ q, limit: Math.min(12, SEARCH_PACKS_MAX_LIMIT), offset: 0 }, ac.signal)
      .then((res) => {
        setHits(res.hits ?? []);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (err instanceof ApogeeSearchDisabledError) {
          setSearchError(err.message);
          setHits([]);
          return;
        }
        setSearchError(err instanceof Error ? err.message : 'Search failed');
        setHits([]);
      })
      .finally(() => setSearching(false));

    return () => ac.abort();
  }, [debouncedQuery]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pickHit = useCallback(
    async (hit: PackSearchHit) => {
      const apogeeId = apogeeIdFromSearchHit(hit);
      if (apogeeId == null) return;

      let row = packRowFromSearchHit(hit);
      if (!row) {
        try {
          row = await apogeeApiService.getPackByApogeeId(apogeeId);
        } catch {
          onChange(apogeeId);
          setQuery('');
          setOpen(false);
          setHits([]);
          return;
        }
      }

      onChange(apogeeId);
      setSelectedPack(row);
      setQuery('');
      setOpen(false);
      setHits([]);
      setHighlightIndex(-1);
    },
    [onChange]
  );

  const clearSelection = () => {
    onChange(null);
    setSelectedPack(null);
    setQuery('');
    setHits([]);
    setOpen(false);
  };

  const showDropdown = Boolean(
    open &&
      !disabled &&
      (searching || searchError || hits.length > 0 || debouncedQuery.trim().length >= 2)
  );

  return (
    <div ref={rootRef} className={cn('space-y-2', className)}>
      {label && (
        <label className="block text-xs font-medium text-glass-400 mb-1.5">{label}</label>
      )}

      {value != null && (
        <div
          className={cn(
            'subcard overflow-hidden',
            compact ? 'p-2.5' : 'p-3'
          )}
        >
          {loadingSelected ? (
            <div className="flex items-center gap-2 text-glass-400 text-sm">
              <Loader className="w-4 h-4 animate-spin shrink-0" />
              Loading pack…
            </div>
          ) : selectedPack ? (
            <div className="flex gap-3 min-w-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border border-glass-200/15 bg-dark-900/50 shrink-0">
                {selectedPack.thumbnail_url ? (
                  <img
                    src={selectedPack.thumbnail_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-glass-500" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={cn('font-semibold text-white truncate', compact ? 'text-sm' : 'text-base')}>
                      {selectedPack.name}
                    </p>
                    <p className="text-[11px] text-glass-500 font-mono truncate">{selectedPack.slug}</p>
                  </div>
                  <Link
                    to={packDetailPath(selectedPack)}
                    className="btn-icon btn-icon-sm shrink-0"
                    title="Open in modpack browser"
                    aria-label="Open in modpack browser"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-glass-500 tabular-nums">
                  <span className="badge badge-accent">
                    <Sparkles className="w-3 h-3" />
                    Apogee #{value}
                  </span>
                  {typeof selectedPack.download_count === 'number' && (
                    <span>{selectedPack.download_count.toLocaleString()} downloads</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-400/90">
              Linked id {value} — pack details could not be loaded. You can clear and search again.
            </p>
          )}
          {!disabled && (
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(true);
                  inputRef.current?.focus();
                }}
                className="glass-button-secondary btn-xs"
              >
                Change pack
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="glass-button-secondary btn-xs"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {(value == null || open) && !disabled && (
        <div className="relative">
          <div className="relative">
            <Search
              className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none',
                open ? 'text-accent-300' : 'text-glass-400'
              )}
            />
            <input
              ref={inputRef}
              type="search"
              role="combobox"
              aria-expanded={showDropdown}
              aria-controls={listboxId}
              aria-autocomplete="list"
              autoComplete="off"
              placeholder="Search modpacks by name, slug, or platform…"
              value={query}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setHighlightIndex(-1);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (!showDropdown || hits.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightIndex((i) => Math.min(hits.length - 1, i + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightIndex((i) => Math.max(0, i - 1));
                } else if (e.key === 'Enter' && highlightIndex >= 0) {
                  e.preventDefault();
                  void pickHit(hits[highlightIndex]);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              className="glass-input w-full pl-9"
            />
          </div>

          {showDropdown && (
            <div
              id={listboxId}
              role="listbox"
              className="absolute z-50 left-0 right-0 mt-1 max-h-80 overflow-y-auto glass-card py-1"
            >
              {searching && (
                <div className="flex items-center justify-center gap-2 py-8 text-glass-400 text-sm">
                  <Loader className="w-4 h-4 animate-spin" />
                  Searching Apogee…
                </div>
              )}
              {!searching && searchError && (
                <div className="alert alert-warning m-2 text-xs">{searchError}</div>
              )}
              {!searching && !searchError && debouncedQuery.trim().length >= 2 && hits.length === 0 && (
                <div className="p-6 text-center text-sm text-glass-500">No packs match that search.</div>
              )}
              {!searching &&
                hits.map((hit, idx) => {
                  const id = apogeeIdFromSearchHit(hit);
                  if (id == null) return null;
                  const thumb = hit._source.thumbnail_url;
                  const active = idx === highlightIndex;
                  return (
                    <button
                      key={`${hit._id}-${idx}`}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onClick={() => void pickHit(hit)}
                      className={cn(
                        'w-full flex gap-3 px-3 py-2.5 text-left transition-colors',
                        active ? 'bg-accent-500/10' : 'hover:bg-glass-200/10'
                      )}
                    >
                      <div className="w-10 h-10 rounded-md overflow-hidden border border-glass-200/15 bg-dark-900/50 shrink-0">
                        {thumb ? (
                          <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-glass-500" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{hitDisplayName(hit)}</p>
                        {hit._source.slug && (
                          <p className="text-[11px] text-glass-500 font-mono truncate">{hit._source.slug}</p>
                        )}
                        {hitSummarySnippet(hit) && (
                          <p className="text-xs text-glass-400 line-clamp-2 mt-1">{hitSummarySnippet(hit)}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-1.5 tabular-nums">
                          <span className="chip">
                            Apogee #{id}
                          </span>
                          {typeof hit._source.mpi_id === 'number' && (
                            <span className="chip text-glass-500">
                              MPI {hit._source.mpi_id}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {description && <p className="text-xs text-glass-500 leading-relaxed">{description}</p>}
    </div>
  );
}
